import { ModalAction, ModalTitle } from '@overlayqa/shared/ui';
import { Button } from '@/components/ui/button';
/**
 * Invite a teammate without leaving the project page (opened by
 * InviteTeamButton, to the left of Settings on IssuesPage's breadcrumb row).
 *
 * It is the Account page's invite form (TeamMembersSection) in a dialog: the
 * same mutation, the same validation and the same copy, all read from
 * lib/inviteEmail.ts so the two surfaces cannot drift. Shell per the dashboard
 * modal contract (modalShellParity / modalCloseButtonParity / modalTypeScale).
 *
 * It STAYS OPEN after a send: adding a second teammate is the common next
 * move, and the confirmation needs somewhere to be read.
 *
 * Analytics, each on a human gesture (one emit per submit, never on render):
 *   invitation_sent        { role, source: 'project_header' }   existing event, new source
 *   invitation_send_failed { source: 'project_header', code }   a refused invite is a wall
 */
import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { UserAdd01Icon } from '@hugeicons/core-free-icons';
import { useModalA11y } from '@/hooks/useModalA11y';
import { ModalPortal } from '@/components/shared/ModalPortal';
import { SelectDropdown } from '@/components/shared/SelectDropdown';
import { InviteRefusalMessage } from '@/components/shared/InviteRefusalMessage';
import { useInviteTeamMember } from '../../hooks/useTeams';
import { capture } from '@/lib/analytics';
import { log } from '@/lib/logger';
import {
  INVITE_FORM_COPY,
  INVITE_ROLE_OPTIONS,
  inviteEmailError,
  isValidInviteEmail,
} from '@/lib/inviteEmail';

interface InviteTeamModalProps {
  /** The team to invite INTO: the one that owns the project on screen. */
  teamId: string;
  onClose: () => void;
  source?: 'project_header' | 'billing_seats';
}

type InviteRole = 'admin' | 'member';

export function InviteTeamModal({ teamId, onClose, source = 'project_header' }: InviteTeamModalProps) {
  const inviteMutation = useInviteTeamMember(teamId);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, dialogRef, onClose);

  // The caret belongs in the email field. An EFFECT, not `autoFocus`: the dev
  // dashboard runs under React.StrictMode, whose simulated unmount makes
  // useModalA11y's close-cleanup hand focus back to the opener; on the re-run
  // the hook finds nothing focused inside and takes the first focusable, the
  // X. `autoFocus` fires once at mount and cannot recover; an effect re-runs
  // with the rest. Order against the hook does not matter: if this runs first
  // the hook sees focus inside and leaves it, if second it simply wins.
  // (Found in real Chromium on the dev server, 2026-09-18 A/B, fixed 20/21.)
  const emailRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  // ...and gets it BACK when a send settles. The field is disabled while the
  // request is pending, a disabled element drops focus, and the dialog stays
  // open precisely so the next address (or the corrected one) can be typed.
  const wasPending = useRef(false);
  useEffect(() => {
    if (wasPending.current && !inviteMutation.isPending) emailRef.current?.focus();
    wasPending.current = inviteMutation.isPending;
  }, [inviteMutation.isPending]);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('member');
  const [formError, setFormError] = useState<string | null>(null);
  // The server's refusal code, so a seat-cap or lapsed-trial refusal can offer
  // "Upgrade plan" (InviteRefusalMessage). Null for a client-side refusal.
  const [formErrorCode, setFormErrorCode] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // A backdrop press closes only when it STARTED on the backdrop. A click's
  // target is the common ancestor of mousedown and mouseup, so selecting the
  // typed address and letting go outside the panel would otherwise read as a
  // backdrop click and throw the address away.
  const pressStartedOnBackdrop = useRef(false);
  const handleBackdropMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    pressStartedOnBackdrop.current = e.target === e.currentTarget;
  };
  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && pressStartedOnBackdrop.current) onClose();
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorCode(null);
    setSuccessMessage(null);

    const refusal = inviteEmailError(email);
    if (refusal) {
      setFormError(refusal);
      return;
    }

    inviteMutation.mutate(
      { email: email.trim(), role },
      {
        onSuccess: (invitation) => {
          setEmail('');
          setRole('member');
          setSuccessMessage(INVITE_FORM_COPY.sent(invitation.email));
          capture('invitation_sent', { role, source });
        },
        onError: (err) => {
          const code = (err as { code?: string }).code ?? 'unknown';
          log.warn('[InviteTeamModal] invite refused', { teamId, code, message: err.message });
          setFormError(err.message || INVITE_FORM_COPY.sendFailed);
          setFormErrorCode(code);
          capture('invitation_send_failed', { source, code });
        },
      },
    );
  };

  const emailValid = isValidInviteEmail(email);

  const overlay = (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-team-title"
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden">
        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between">
          <ModalTitle icon={UserAdd01Icon} id="invite-team-title" className="oq-dialog__title">Invite team</ModalTitle>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* noValidate: the browser's own bubble on type="email" is invisible in
            replays and would pre-empt the form's sentence. */}
        <form className="px-6 pt-4 pb-5" noValidate onSubmit={handleSubmit}>
          <fieldset className="mb-5">
            <label htmlFor="invite-team-email" className="field-label">
              {INVITE_FORM_COPY.emailLabel}
            </label>
            <input
              id="invite-team-email"
              type="email"
              autoComplete="off"
              className="input input-lg w-full"
              placeholder={INVITE_FORM_COPY.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={inviteMutation.isPending}
              ref={emailRef}
            />
          </fieldset>

          <fieldset className="mb-3">
            <label htmlFor="invite-team-role" className="field-label">
              {INVITE_FORM_COPY.roleLabel}
            </label>
            <SelectDropdown
              id="invite-team-role"
              value={role}
              onChange={(value) => setRole(value as InviteRole)}
              disabled={inviteMutation.isPending}
              options={INVITE_ROLE_OPTIONS}
              // The 40px tier, like the input-lg email field above it. The
              // default dense tier left a 32px picker under a 40px field
              // (read off the first browser frame, 2026-09-18).
              size="lg"
            />
          </fieldset>

          {/* .field-hint, the shared class for modal supporting copy (bodyCopySystem guard). */}
          <p className="field-hint">{INVITE_FORM_COPY.helper}</p>
          {formError && (
            <InviteRefusalMessage message={formError} code={formErrorCode} source="project_header" />
          )}
          {successMessage && (
            <p className="mt-2 text-xs text-green-700" role="status">{successMessage}</p>
          )}

          <div className="mt-5 flex gap-3 justify-end">
            <Button type="button" variant="secondary" context="standard" onClick={onClose}><ModalAction kind="cancel">
              Cancel
            </ModalAction></Button>
            <Button
              type="submit" context="standard"
              disabled={inviteMutation.isPending || !emailValid}
              title={emailValid ? undefined : INVITE_FORM_COPY.submitDisabledTitle}
            ><ModalAction kind="invite">
              {inviteMutation.isPending ? INVITE_FORM_COPY.submitting : INVITE_FORM_COPY.submit}
            </ModalAction></Button>
          </div>
        </form>
      </div>
    </div>
  );

  return <ModalPortal>{overlay}</ModalPortal>;
}
